import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    FormControl,
    IconButton,
    MenuItem,
    Select,
    Snackbar,
    Stack,
    Tab,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import { ArrowBackIos, ArrowForwardIos, Check, Close, Edit, WarningAmber } from '@mui/icons-material';
import { Trans, useTranslation } from 'react-i18next';

import AdmiMap from 'src/common/components/AdmiMap';
import { MainAddressSelector } from './MainAddressSelector';
import { BuildingsDeckLayer } from 'src/features/real-estate/BuildingsDeckLayer';
import { Building } from 'src/features/real-estate/buildings.dto';
import { useAppDispatch, useAppSelector } from 'src/redux-app-hooks';
import { NotificationSeverity, showSnackbar } from 'src/features/notifications/notifications.slice';
import { isProblemDetailTextOr } from 'src/common/ProblemDetail';
import {
    addBuildingToAsset,
    clearError,
    clearSelectedRea,
    createRealEstateAsset,
    fetchRealEstateAsset,
    fetchRealEstateAssetServices,
    loadBuildingsWithAssets,
    removeBuildingFromAsset,
    updateRealEstateAsset,
} from './real-estate-asset-management.slice';
import { ServicesTab } from './components/ServicesTab';
import { SteckbriefeTab } from './components/SteckbriefeTab';
import { DataCollectionTab } from './components/DataCollectionTab';
import { buildMeterMarkerLayers, MeterMarkerPoint } from './components/MeterMarkerLayers';
import { HistoryTab } from './components/HistoryTab';
import { admiDataCollectionService } from './admi-data-collection.service';
import { mergeCatalogWithProductGroups } from './components/data-collection.helpers';
import { GroupsForProduct, ProductRequirementGroup } from './data-collection.dto';
import ImportBuildingDialog from './components/ImportBuildingDialog';
import useGoogleMapsApiLoader from 'src/infrastructure/google/useGoogleMapsApiLoader';
import { ReaTab, StaticReaTab, isGroupTab, parseReaTab, tabKey } from './rea-page-params';

const INITIAL_CENTER: google.maps.LatLngLiteral = { lat: 51.211443266, lng: 6.803755898 };

type ReaMode = 'idle' | 'view' | 'noRea' | 'create' | 'editAssociations';

function stripCountry(address: string): string {
    return address.replace(/, Germany$/, '');
}

/**
 * Locate the meters LIST field and its building-select sub-field by structure
 * (no hardcoded codes): a `buildings`-sourced field whose parent is a LIST.
 * Returns the requirement codes needed to read saved meter assignments out of
 * collected items. `null` when the group set has no such field.
 */
function findMeterBuildingFieldCodes(
    groups: ProductRequirementGroup[],
): { listCode: string; buildingsSubCode: string } | null {
    for (const group of groups) {
        const fields = group.requirementFields;
        for (const f of fields) {
            if (f.optionSource === 'buildings' && f.parentFieldId) {
                const parent = fields.find((p) => p.id === f.parentFieldId);
                if (parent?.fieldType === 'list') {
                    return { listCode: parent.code, buildingsSubCode: f.code };
                }
            }
        }
    }
    return null;
}

function buildingCenter(b: Building): google.maps.LatLngLiteral | null {
    if (b.geometry) {
        const ring =
            b.geometry.type === 'Polygon'
                ? b.geometry.coordinates[0]
                : b.geometry.type === 'MultiPolygon'
                  ? b.geometry.coordinates[0]?.[0]
                  : null;
        if (ring && ring.length > 0) {
            const lngSum = ring.reduce((s, c) => s + c[0], 0);
            const latSum = ring.reduce((s, c) => s + c[1], 0);
            return { lat: latSum / ring.length, lng: lngSum / ring.length };
        }
    }
    if (b.box && b.box.length >= 4) {
        return { lat: (b.box[1] + b.box[3]) / 2, lng: (b.box[0] + b.box[2]) / 2 };
    }
    return null;
}

function haversineMetres(a: google.maps.LatLngLiteral, b: google.maps.LatLngLiteral): number {
    const R = 6_371_000;
    const lat1 = (a.lat * Math.PI) / 180;
    const lat2 = (b.lat * Math.PI) / 180;
    const deltaLat = ((b.lat - a.lat) * Math.PI) / 180;
    const deltaLng = ((b.lng - a.lng) * Math.PI) / 180;
    const x = Math.sin(deltaLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
}

function isFarFromSelection(clicked: Building, selected: Building[]): boolean {
    if (selected.length === 0) return false;
    const clickedCenter = buildingCenter(clicked);
    if (!clickedCenter) return false;
    const minDist = Math.min(
        ...selected.map((b) => {
            const c = buildingCenter(b);
            return c ? haversineMetres(clickedCenter, c) : Infinity;
        }),
    );
    return minDist > 100;
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
    return (
        <>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {label}
            </Typography>
            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                {value ?? <span style={{ color: '#aaa' }}>–</span>}
            </Typography>
        </>
    );
}

function TabLabel({ label, warning }: { label: string; warning: boolean }) {
    if (!warning) return <>{label}</>;
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {label}
            <WarningAmber sx={{ fontSize: 14 }} color="warning" />
        </Box>
    );
}

export default function RealEstateAssetManagementPage() {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const {
        buildings,
        realEstateAssets,
        selectedRea,
        isLoadingBuildings,
        isLoadingRea,
        isMutating,
        errorMessage,
        lastLocationSearch,
        reaServices,
        reaServicesLoadedForId,
        isLoadingReaServices,
    } = useAppSelector((state) => state.realEstateAssetManagement);

    const [selectedBuildings, setSelectedBuildings] = useState<Building[]>([]);
    const [currentBuildingIndex, setCurrentBuildingIndex] = useState(0);
    const [reaMode, setReaMode] = useState<ReaMode>('idle');
    const [dataCollectionGroups, setDataCollectionGroups] = useState<ProductRequirementGroup[]>([]);
    const [isLoadingDataCollectionGroups, setIsLoadingDataCollectionGroups] = useState(false);

    const [buildingAddressMap, setBuildingAddressMap] = useState<Map<string, string>>(new Map());
    const [buildingMunicipalityMap, setBuildingMunicipalityMap] = useState<Map<string, string>>(new Map());

    const [newReaName, setNewReaName] = useState('');
    const [newReaFunctionCode, setNewReaFunctionCode] = useState('');
    const [newReaNotes, setNewReaNotes] = useState('');
    /** Either a building address from the dropdown, or the sentinel '__other__'. */
    const [newMainAddressSelection, setNewMainAddressSelection] = useState('');
    const [newCustomMainAddress, setNewCustomMainAddress] = useState('');

    const [isEditingName, setIsEditingName] = useState(false);
    const [editNameValue, setEditNameValue] = useState('');

    const [isEditingFunction, setIsEditingFunction] = useState(false);
    const [editFunctionCode, setEditFunctionCode] = useState<string>('');

    const [isEditingNotes, setIsEditingNotes] = useState(false);
    const [editNotesValue, setEditNotesValue] = useState('');

    const [isEditingMainAddress, setIsEditingMainAddress] = useState(false);
    /** Either a building address from the dropdown, or the sentinel '__other__'. */
    const [editMainAddressSelection, setEditMainAddressSelection] = useState('');
    const [editCustomMainAddress, setEditCustomMainAddress] = useState('');

    const [hoveredBuildingId, setHoveredBuildingId] = useState<string | null>(null);

    const [createConfirmOpen, setCreateConfirmOpen] = useState(false);
    const [createConfirmAddresses, setCreateConfirmAddresses] = useState<string[]>([]);
    const [createConfirmHasConflict, setCreateConfirmHasConflict] = useState(false);
    const createPendingRef = useRef<(() => void) | null>(null);

    const [conflictReaError, setConflictReaError] = useState(false);
    const [conflictMunicipalityError, setConflictMunicipalityError] = useState(false);
    const [conflictUnassociatedError, setConflictUnassociatedError] = useState(false);

    const [unsavedChangesOpen, setUnsavedChangesOpen] = useState(false);
    const unsavedPendingRef = useRef<(() => void) | null>(null);
    const [isServicesTabEditing, setIsServicesTabEditing] = useState(false);
    const [isDataCollectionDirty, setIsDataCollectionDirty] = useState(false);
    // Meter→building assignments lifted from the active data-collection tab,
    // used to render numbered markers on the map.
    const [meterMarkers, setMeterMarkers] = useState<{ meterNumber: number; buildingIds: string[] }[]>([]);
    // Building IDs connected to a meter per the *saved* Energy data (read from
    // collected items via API — independent of the Energy tab's live state).
    // Used to block removing such a building on the General page.
    const [meterConnectedBuildingIds, setMeterConnectedBuildingIds] = useState<Set<string>>(new Set());
    const [meterConnectionError, setMeterConnectionError] = useState(false);
    // Monotonic id of the latest meter-connection load; an awaited response bails
    // if a newer load has superseded it (guards against out-of-order resolution).
    const meterIdsRequestSeq = useRef(0);

    const [importDialogOpen, setImportDialogOpen] = useState(false);
    const [mapCenter, setMapCenter] = useState<google.maps.LatLngLiteral>(INITIAL_CENTER);

    // Warning when adding a building whose address differs from existing ones (editAssociations)
    const [addAddressWarningOpen, setAddAddressWarningOpen] = useState(false);
    const [addAddressWarningExisting, setAddAddressWarningExisting] = useState<string[]>([]);
    const [addAddressWarningNew, setAddAddressWarningNew] = useState<string[]>([]);

    // Warning when adding a building that is more than 100 m from the current selection
    const [distanceWarningOpen, setDistanceWarningOpen] = useState(false);
    const [distanceWarningPendingBuilding, setDistanceWarningPendingBuilding] = useState<Building | null>(null);

    // Warning when removing buildings causes the current main address to disappear
    const [removeAddressWarningOpen, setRemoveAddressWarningOpen] = useState(false);
    const [removeAddressWarningRemoved, setRemoveAddressWarningRemoved] = useState<string[]>([]);
    const [removeAddressWarningRemaining, setRemoveAddressWarningRemaining] = useState<string[]>([]);

    // Tracks whether the user must pick a new main address before saving (editAssociations)
    const [editAssociationsNeedsNewAddress, setEditAssociationsNeedsNewAddress] = useState(false);
    /** Either a building address from the dropdown, or the sentinel '__other__'. */
    const [editAssociationsNewAddress, setEditAssociationsNewAddress] = useState('');
    const [editAssociationsNewCustomAddress, setEditAssociationsNewCustomAddress] = useState('');

    // ── Derived state ───────────────────────────────────────────────────────

    const associatedReaSummary =
        selectedBuildings.length > 0
            ? (realEstateAssets.find((r) => r.buildings.some((b) => b.id === selectedBuildings[0].id)) ?? null)
            : null;

    const currentBuilding = selectedBuildings[currentBuildingIndex] ?? null;

    /**
     * Distinct building functions among the buildings currently assigned to the selected REA.
     * Used to populate the "Real Estate Asset function" dropdown — a REA's function must be
     * one of the functions of its member buildings.
     */
    const reaFunctionOptions: { code: string; name: string }[] = useMemo(() => {
        if (!selectedRea) return [];
        const reaBuildingIds = new Set(selectedRea.buildings.map((b) => b.id));
        const byCode = new Map<string, string>();
        for (const b of buildings) {
            if (reaBuildingIds.has(b.id) && b.buildingFunction?.code) {
                byCode.set(b.buildingFunction.code, b.buildingFunction.name ?? b.buildingFunction.code);
            }
        }
        return [...byCode.entries()].map(([code, name]) => ({ code, name }));
    }, [selectedRea, buildings]);

    const selectedReaFunctionName = useMemo(
        () =>
            selectedRea?.buildingFunctionCode
                ? (reaFunctionOptions.find((o) => o.code === selectedRea.buildingFunctionCode)?.name ??
                  selectedRea.buildingFunctionCode)
                : null,
        [selectedRea, reaFunctionOptions],
    );

    /**
     * Unique resolved addresses for the buildings of the currently-selected REA.
     * Drives the Main Address dropdown options in the General tab inline edit.
     */
    const reaAddressOptions: string[] = useMemo(() => {
        if (!selectedRea) return [];
        const addrs = selectedRea.buildings
            .map((b) => buildingAddressMap.get(b.id))
            .filter((a): a is string => Boolean(a) && a !== '–');
        return [...new Set(addrs)];
    }, [selectedRea, buildingAddressMap]);

    /**
     * Unique resolved addresses for the buildings currently selected on the map.
     * Drives the Main Address dropdown options in the Create form.
     */
    const selectedAddressOptions: string[] = useMemo(() => {
        const addrs = selectedBuildings
            .map((b) => buildingAddressMap.get(b.id))
            .filter((a): a is string => Boolean(a) && a !== '–');
        return [...new Set(addrs)];
    }, [selectedBuildings, buildingAddressMap]);

    /**
     * Distinct addresses for the selected REA's buildings, including a single
     * "Loading…" placeholder while geocoding is still in flight. Rendered as the
     * read-only "Building addresses" list in the General tab.
     */
    const reaDisplayAddresses: string[] = useMemo(() => {
        if (!selectedRea) return [];
        return [
            ...new Set(selectedRea.buildings.map((b) => stripCountry(buildingAddressMap.get(b.id) ?? t('Loading…')))),
        ].sort((a, b) => a.localeCompare(b, 'de'));
    }, [selectedRea, buildingAddressMap, t]);

    /**
     * Maps each building ID that belongs to a REA to its map tooltip text.
     * - Selected REA: "{name}\n({N}/{M}) {function}\n{geocoded address}"
     * - Other REAs:   "{name}"
     */
    const reaTooltipMap = useMemo(() => {
        const map = new Map<string, string>();
        for (const rea of realEstateAssets) {
            const isSelected = selectedRea?.id === rea.id;
            if (isSelected && selectedRea) {
                selectedRea.buildings.forEach((b, index) => {
                    const functionName = buildings.find((full) => full.id === b.id)?.buildingFunction?.name ?? '';
                    const address = stripCountry(buildingAddressMap.get(b.id) ?? t('Address loading...'));
                    map.set(
                        b.id,
                        `${selectedRea.name}\n(${index + 1}/${selectedRea.buildings.length}) ${functionName}\n${address}`,
                    );
                });
            } else {
                for (const b of rea.buildings) {
                    map.set(b.id, rea.name);
                }
            }
        }
        return map;
    }, [realEstateAssets, selectedRea, buildings, buildingAddressMap, t]);

    // ── Deep-link from REA collection view ─────────────────────────────────
    //
    // When the page is opened with `?reaId=<uuid>`, hydrate the REA into
    // the slice, geocode its mainAddress to pan the map, then pre-select the
    // REA's buildings once they arrive in `buildings` so the existing flow
    // (the effect on line ~334) moves the page into `view` mode. The `?reaId`
    // param is kept in the URL (shareable / survives refresh). The geocode-pan
    // and the buildings auto-select are each one-shot per landing (guarded by
    // `deepLinkPanned` / `deepLinkBuildingsSelected`, both reset when `reaId`
    // changes), so they don't re-fire against the user's current map view
    // mid-session; a refresh simply re-runs the full hydrate, which is the
    // intended deep-link restore.

    const [searchParams, setSearchParams] = useSearchParams();
    const deepLinkReaId = searchParams.get('reaId');
    const deepLinkBuildingsSelected = useRef(false);
    // One-shot guard so the deep-link geocode pans the map exactly once per
    // landing. Without it, deselecting all buildings (which clears
    // `selectedRea`) and then re-selecting the deep-linked REA's buildings
    // would re-fire the geocode effect and yank the map back to the REA,
    // overriding wherever the user has since panned.
    const deepLinkPanned = useRef(false);
    // The `?reaId` value this page wrote itself to REFLECT an in-page selection
    // (GEN-701 QA feedback #1: the URL always carries the selected REA + tab).
    // A self-written param is not a deep-link landing — the hydration effects
    // (geocode-pan + buildings auto-select) must not fire for it, or the map
    // would yank away from the buildings the user just clicked.
    const selfWrittenReaIdRef = useRef<string | null>(null);
    // True once the current landing has reached `view` mode. Until then an
    // idle/noRea mode is hydration in transit, not the user moving away, and
    // the URL-reflect effect must not clear `?reaId`/`?tab`. The ref exists
    // because `deepLinkBuildingsSelected` cannot answer this: returning from
    // the routed Variant-form page while the store still holds this REA's
    // buildings, the auto-select flips that ref in the very first effects
    // pass — one to two commits before `reaMode` catches up — and the
    // clearing effect (same pass, `reaMode` still 'idle') would strip the
    // `?tab=steckbriefe` the Variant form just navigated back with.
    const landingSettledRef = useRef(false);
    // Id of the last REA this page reached `view` mode on. Keyed on the id
    // (never reset) so the deep-link fetch guard below reads it correctly
    // regardless of effect order — unlike the boolean guards above, which
    // rely on the `[deepLinkReaId]` effect resetting them first.
    const viewedReaIdRef = useRef<string | null>(null);
    const googleMapsLoader = useGoogleMapsApiLoader();
    const [deepLinkPanTo, setDeepLinkPanTo] = useState<google.maps.LatLngLiteral | null>(null);

    useEffect(() => {
        if (deepLinkReaId !== null && deepLinkReaId === selfWrittenReaIdRef.current) {
            // URL write reflecting an in-page selection — keep the one-shot
            // hydration guards armed instead of re-arming them. The page is
            // already viewing this REA, so the landing counts as settled.
            deepLinkBuildingsSelected.current = true;
            deepLinkPanned.current = true;
            landingSettledRef.current = true;
            setDeepLinkPanTo(null);
            return;
        }
        deepLinkBuildingsSelected.current = false;
        deepLinkPanned.current = false;
        landingSettledRef.current = false;
        // Drop the previous REA's pan target so a re-armed pan (e.g. after a
        // map-instance swap) can't briefly recenter on the old REA before the
        // new one is geocoded.
        setDeepLinkPanTo(null);
    }, [deepLinkReaId]);

    // ── URL-driven tab (GEN-701 QA feedback #1) ─────────────────────────────
    //
    // The active tab lives in `?tab=` (the tab key, `general` omitted) so any
    // REA view is deep-linkable and the routed Variant-form page's Back can
    // restore "this REA, Steckbriefe tab" exactly. `replace` navigation — tab
    // switches don't grow the history.

    const activeReaTab: ReaTab = useMemo(() => parseReaTab(searchParams.get('tab')), [searchParams]);

    const setActiveReaTab = useCallback(
        (next: ReaTab) => {
            setSearchParams(
                (prev) => {
                    const params = new URLSearchParams(prev);
                    const key = tabKey(next);
                    if (key === 'general') params.delete('tab');
                    else params.set('tab', key);
                    return params;
                },
                { replace: true },
            );
        },
        [setSearchParams],
    );

    // ── Effects ─────────────────────────────────────────────────────────────

    useEffect(() => {
        // Skip the initial INITIAL_CENTER load when we have a deep-link to
        // hydrate — the deep-link's own pan will trigger `loadBuildingsWithAssets`
        // for the right area. Without this guard we'd briefly fetch buildings
        // around Düsseldorf, then re-fetch around the REA's actual location.
        if (deepLinkReaId) return;
        handleCenterChanged(INITIAL_CENTER);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Kick off REA detail load on first render with `?reaId=` present.
    //
    // Only while a deep-link landing is hydrating: once the page has viewed
    // that REA (or the param is self-written), `?reaId` merely REFLECTS the
    // selection and the selection flow owns loading. Without this guard,
    // deselecting an REA clears `selectedRea` one commit before `?reaId` is
    // removed, re-fetching the old REA; selecting another REA then has this
    // effect (fetch what the URL names) and the URL-reflect effect (write what
    // `selectedRea` names) ping-pong `?reaId` between the two REAs forever.
    useEffect(() => {
        if (!deepLinkReaId) return;
        if (deepLinkReaId === selfWrittenReaIdRef.current || deepLinkReaId === viewedReaIdRef.current) return;
        if (selectedRea?.id === deepLinkReaId) return;
        dispatch(fetchRealEstateAsset(deepLinkReaId));
    }, [deepLinkReaId, selectedRea?.id, dispatch]);

    // When the deep-linked REA detail arrives, geocode its mainAddress and
    // pan the map there. `handleCenterChanged` triggers the building load.
    //
    // Goes through `useGoogleMapsApiLoader` + `loader.importLibrary('geocoding')`
    // — using `new google.maps.Geocoder()` directly races against the Maps
    // script load and fails silently on cold cache (this effect fires as soon
    // as `fetchRealEstateAsset` resolves, which can be before AdmiMap has the
    // script ready).
    //
    // Pans at most once per deep-link landing: the `deepLinkPanned` ref short-
    // circuits re-runs (e.g. when `selectedRea` cycles away and back to the
    // deep-linked REA after a deselect-all), so a one-time landing pan never
    // overrides where the user has since moved the map. The ref is reset when
    // `reaId` changes, so navigating to a different REA pans again.
    useEffect(() => {
        if (!deepLinkReaId || !selectedRea || selectedRea.id !== deepLinkReaId) return;
        if (deepLinkPanned.current) return;
        let cancelled = false;
        (async () => {
            try {
                const { Geocoder } = (await googleMapsLoader.importLibrary(
                    'geocoding',
                )) as google.maps.GeocodingLibrary;
                if (cancelled) return;
                const geocoder = new Geocoder();
                const response = await geocoder.geocode({ address: selectedRea.mainAddress }).catch(() => undefined);
                if (cancelled || !response || response.results.length === 0) return;
                const result = response.results[0];
                const center = {
                    lat: result.geometry.location.lat(),
                    lng: result.geometry.location.lng(),
                };
                // Two responsibilities: kick off the buildings load for that
                // location (handleCenterChanged) AND pan the map there
                // (setDeepLinkPanTo, consumed via AdmiMap's `panTo` prop).
                handleCenterChanged(center);
                setDeepLinkPanTo(center);
                // Mark only after a successful pan, so a cancelled / failed
                // geocode can be retried on the next run.
                deepLinkPanned.current = true;
            } catch {
                // Loader failure (network, invalid API key, etc.) — leave the
                // map at INITIAL_CENTER. The REA panel still shows because
                // `selectedRea` was loaded by the previous effect.
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [selectedRea?.id, deepLinkReaId, googleMapsLoader]); // eslint-disable-line react-hooks/exhaustive-deps

    // After buildings load near the geocoded REA, pre-select the REA's
    // buildings — drives the existing selection-driven flow into `view` mode.
    // One-shot per landing via `deepLinkBuildingsSelected` (reset when `reaId`
    // changes); the `?reaId` param is left in the URL so the deep-link is
    // shareable and survives a refresh.
    useEffect(() => {
        if (!deepLinkReaId || deepLinkBuildingsSelected.current) return;
        if (!selectedRea || selectedRea.id !== deepLinkReaId) return;
        if (buildings.length === 0) return;
        const reaBuildingIds = new Set(selectedRea.buildings.map((b) => b.id));
        const matching = buildings.filter((b) => reaBuildingIds.has(b.id));
        if (matching.length === 0) return;
        deepLinkBuildingsSelected.current = true;
        setSelectedBuildings(matching);
    }, [buildings, selectedRea?.id, deepLinkReaId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setCurrentBuildingIndex((i) =>
            selectedBuildings.length === 0 ? 0 : Math.min(i, selectedBuildings.length - 1),
        );
    }, [selectedBuildings.length]);

    useEffect(() => {
        // Don't override the mode while the user is in an active editing flow —
        // adding/removing buildings during editAssociations or create must preserve that mode.
        if (reaMode === 'editAssociations' || reaMode === 'create') return;

        if (selectedBuildings.length === 0) {
            setReaMode('idle');
            // Drop the stale REA so re-selecting the *same* REA produces a real
            // id transition that re-fires the services + requirement-groups fetch chain.
            // Guard each dispatch on its own state so an already-cleared store doesn't
            // trigger redundant updates/re-renders every time this effect re-runs.
            if (selectedRea || reaServices.length > 0 || reaServicesLoadedForId !== null) {
                dispatch(clearSelectedRea());
            }
            if (errorMessage) {
                dispatch(clearError());
            }
            return;
        }
        if (associatedReaSummary) {
            if (!selectedRea || selectedRea.id !== associatedReaSummary.id) {
                dispatch(fetchRealEstateAsset(associatedReaSummary.id));
            }
            setReaMode('view');
        } else {
            setReaMode('noRea');
        }
    }, [selectedBuildings, associatedReaSummary?.id, reaMode]); // eslint-disable-line react-hooks/exhaustive-deps

    const prevAssociatedReaIdRef = useRef<string | undefined>(undefined);
    useEffect(() => {
        const previousReaId = prevAssociatedReaIdRef.current;
        prevAssociatedReaIdRef.current = associatedReaSummary?.id;
        // Reset the (URL-driven) tab only on a real REA-to-REA transition —
        // never on the first association after mount, which would wipe a
        // deep-linked `?tab` while the landing is still hydrating.
        if (previousReaId !== undefined && previousReaId !== associatedReaSummary?.id) {
            setActiveReaTab('general');
        }
        setIsEditingName(false);
        setIsEditingFunction(false);
        setIsEditingNotes(false);
        setIsEditingMainAddress(false);
    }, [associatedReaSummary?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    // `selectedRea`'s reference changes on any REA edit (name, notes, …); key the
    // loader on the id alone so editing those fields doesn't trigger a redundant refetch.
    const selectedReaId = selectedRea?.id;

    // Mark the landing settled once the page is actually viewing the REA the
    // URL names. `selectedReaId` is in the deps so the ref also flips when the
    // deep-linked detail arrives while `reaMode` is already 'view'.
    useEffect(() => {
        if (reaMode !== 'view') return;
        landingSettledRef.current = true;
        if (selectedReaId) viewedReaIdRef.current = selectedReaId;
    }, [reaMode, selectedReaId]);

    // Reflect the in-page selection into `?reaId` (GEN-701 QA feedback #1): the
    // page URL always names the REA being viewed, so it is shareable at any
    // time and survives the round-trip through the routed Variant-form page.
    // Self-written values are recorded in `selfWrittenReaIdRef` BEFORE the
    // write so the `[deepLinkReaId]` effect above keeps the hydration guards
    // armed (no pan / re-select for a selection the user just made). `replace`
    // navigation — selection changes don't grow the history.
    useEffect(() => {
        if (reaMode === 'view' && selectedReaId) {
            if (deepLinkReaId !== selectedReaId) {
                selfWrittenReaIdRef.current = selectedReaId;
                setSearchParams(
                    (prev) => {
                        const params = new URLSearchParams(prev);
                        params.set('reaId', selectedReaId);
                        return params;
                    },
                    { replace: true },
                );
            }
            return;
        }
        if ((reaMode === 'idle' || reaMode === 'noRea') && deepLinkReaId) {
            // Don't clear while a deep-link landing is still hydrating (it has
            // not reached `view` mode yet) — only after the user actively
            // moved away from the REA the URL names. Settledness, not
            // `deepLinkBuildingsSelected`, is the discriminator here: see the
            // `landingSettledRef` declaration for the round-trip race.
            const hydrationInProgress = deepLinkReaId !== selfWrittenReaIdRef.current && !landingSettledRef.current;
            if (hydrationInProgress) return;
            selfWrittenReaIdRef.current = null;
            setSearchParams(
                (prev) => {
                    const params = new URLSearchParams(prev);
                    params.delete('reaId');
                    params.delete('tab');
                    return params;
                },
                { replace: true },
            );
        }
    }, [reaMode, selectedReaId, deepLinkReaId, setSearchParams]);

    // Load the saved meter→building connections (from collected items via API) so
    // the General page can block removing a building a meter still references. Only
    // the *saved* Energy data matters — unsaved Energy edits are gated before the
    // user can navigate away, so the General tab needs nothing from its live state.
    const loadMeterConnectedBuildingIds = useCallback(async () => {
        // Wait until the current REA's services and requirement groups have finished
        // loading. Running earlier would fire against stale/empty groups — a redundant
        // fetch, or wiping the set before the real data arrives — and could let an
        // out-of-order response land. (Same readiness gate the groups fetch uses.)
        if (!selectedReaId || reaServicesLoadedForId !== selectedReaId || isLoadingDataCollectionGroups) {
            return;
        }
        // Tag this load; any later load bumps the seq so a response that resolves
        // out of order (e.g. a slow fetch after a fast REA switch) is dropped.
        const seq = ++meterIdsRequestSeq.current;
        const codes = findMeterBuildingFieldCodes(dataCollectionGroups);
        if (!codes) {
            setMeterConnectedBuildingIds(new Set());
            return;
        }
        try {
            const resp = await admiDataCollectionService.listCollectedItems(selectedReaId);
            if (seq !== meterIdsRequestSeq.current) return; // superseded by a newer load
            const ids = new Set<string>();
            for (const item of resp.collectedItems) {
                if (item.requirementCode !== codes.listCode || !item.valueText) continue;
                try {
                    const entries: unknown = JSON.parse(item.valueText);
                    if (!Array.isArray(entries)) continue;
                    for (const entry of entries) {
                        if (!entry || typeof entry !== 'object') continue;
                        const raw = (entry as Record<string, unknown>)[codes.buildingsSubCode];
                        if (Array.isArray(raw)) {
                            for (const id of raw) ids.add(String(id));
                        } else if (typeof raw === 'string' && raw) {
                            ids.add(raw);
                        }
                    }
                } catch {
                    // Skip a malformed entry blob rather than failing the whole load.
                }
            }
            setMeterConnectedBuildingIds(ids);
        } catch {
            // Keep the last-known set, but tell the user: the "can't remove a building
            // that's connected to a meter" guard couldn't refresh and may be unavailable.
            dispatch(
                showSnackbar({
                    message: t('Could not load meter connections — building-removal protection may be unavailable.'),
                    severity: NotificationSeverity.Error,
                }),
            );
        }
    }, [selectedReaId, dataCollectionGroups, reaServicesLoadedForId, isLoadingDataCollectionGroups, dispatch, t]);

    useEffect(() => {
        void loadMeterConnectedBuildingIds();
    }, [loadMeterConnectedBuildingIds]);

    // Clear stale REA-scoped state immediately on REA switch, so a failed or in-flight
    // load of the new REA's data can't leave the previous REA's data showing. Keyed on
    // `selectedReaId` — the REA actually loaded into the panel — NOT on
    // `associatedReaSummary?.id` (the REA of the selected buildings). Those two diverge
    // in time when an REA is opened from the collection view / deep-link: `selectedRea`
    // loads first (services → groups → data-collection tabs appear), then the buildings
    // auto-select and bump `associatedReaSummary?.id`. Clearing the groups on that later
    // bump would wipe the freshly-loaded tabs, and the groups effect wouldn't re-run
    // (its deps are unchanged) — so the tabs only came back when the Services tab forced
    // a services re-fetch. The loader repopulates once the new REA's services + groups
    // are ready.
    useEffect(() => {
        setMeterConnectedBuildingIds(new Set());
        setDataCollectionGroups([]);
    }, [selectedReaId]);

    // Fetch the full requirement catalog (always shown, GEN-653) plus the groups
    // for every distinct product attached to the REA's services — the per-product
    // copies carry criticality/provenance and are overlaid onto the catalog.
    // Groups sharing a `code` become a single tab. Skip while services are still
    // loading.
    useEffect(() => {
        if (!selectedRea) return;
        if (reaServicesLoadedForId !== selectedRea.id) return;
        const productIds = new Set<string>();
        for (const deal of reaServices) {
            for (const svc of deal.services) {
                if (svc.productId) productIds.add(svc.productId);
            }
        }
        let cancelled = false;
        setIsLoadingDataCollectionGroups(true);
        const ids = [...productIds];
        Promise.allSettled([
            admiDataCollectionService.getRequirementGroupsCatalog(),
            ...ids.map((id) => admiDataCollectionService.getProductRequirementGroups(id)),
        ])
            .then(([catalogResult, ...productResults]) => {
                if (cancelled) return;
                // Partial failure is not fatal — render what we got and tell the
                // user some tabs may be missing. Stale UI without warning would
                // be worse than a noisy snackbar.
                const ok: GroupsForProduct[] = [];
                const failures: unknown[] = [];
                if (catalogResult.status === 'rejected') failures.push(catalogResult.reason);
                productResults.forEach((r, i) => {
                    if (r.status === 'fulfilled') ok.push({ productId: ids[i], groups: r.value });
                    else failures.push(r.reason);
                });
                const catalog = catalogResult.status === 'fulfilled' ? catalogResult.value : [];
                setDataCollectionGroups(mergeCatalogWithProductGroups(catalog, ok));
                if (failures.length > 0) {
                    const first = isProblemDetailTextOr(failures[0], t('Failed to load requirements'));
                    const message =
                        failures.length === 1
                            ? first
                            : t('{{count}} products failed to load requirements: {{first}}', {
                                  count: failures.length,
                                  first,
                              });
                    dispatch(showSnackbar({ message, severity: NotificationSeverity.Error }));
                }
            })
            .finally(() => {
                if (!cancelled) setIsLoadingDataCollectionGroups(false);
            });
        return () => {
            cancelled = true;
        };
    }, [selectedRea, reaServices, reaServicesLoadedForId]);

    useEffect(() => {
        const toGeocode = selectedBuildings.filter((b) => !buildingAddressMap.has(b.id));
        if (toGeocode.length === 0) return;

        const geocoder = new google.maps.Geocoder();
        let cancelled = false;

        // Sequential with a small gap — avoids bursting the Geocoder per-second limit
        // when a REA with many buildings is opened.
        (async () => {
            for (const building of toGeocode) {
                if (cancelled) return;
                const center = buildingCenter(building);
                if (!center) {
                    setBuildingAddressMap((prev) => new Map(prev).set(building.id, '–'));
                    setBuildingMunicipalityMap((prev) => new Map(prev).set(building.id, '–'));
                    continue;
                }
                try {
                    const response = await geocoder.geocode({ location: center });
                    if (cancelled) return;
                    const first = response.results[0];
                    const address = first?.formatted_address ?? '–';
                    // `locality` is the municipality/city (e.g. "Düsseldorf"); fall back to
                    // postal_town or administrative_area_level_2 for areas where locality
                    // isn't populated by Google.
                    const locality =
                        first?.address_components.find((c) => c.types.includes('locality'))?.long_name ??
                        first?.address_components.find((c) => c.types.includes('postal_town'))?.long_name ??
                        first?.address_components.find((c) => c.types.includes('administrative_area_level_2'))
                            ?.long_name ??
                        '–';
                    setBuildingAddressMap((prev) => new Map(prev).set(building.id, address));
                    setBuildingMunicipalityMap((prev) => new Map(prev).set(building.id, locality));
                } catch {
                    if (cancelled) return;
                    setBuildingAddressMap((prev) => new Map(prev).set(building.id, '–'));
                    setBuildingMunicipalityMap((prev) => new Map(prev).set(building.id, '–'));
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [selectedBuildings]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (errorMessage) {
            dispatch(showSnackbar({ message: errorMessage, severity: NotificationSeverity.Error }));
            dispatch(clearError());
        }
    }, [errorMessage]); // eslint-disable-line react-hooks/exhaustive-deps

    // Fetch services whenever the selected REA changes — not when the tab is
    // opened — so the Services-tab warning icon is accurate before the user
    // navigates there. The slice clears stale data on the pending action.
    useEffect(() => {
        if (selectedRea) {
            dispatch(fetchRealEstateAssetServices(selectedRea.id));
        }
    }, [selectedRea?.id, dispatch]);

    // Only show the warning once we've actually fetched services for *this* REA —
    // otherwise it flashes between REA selection and the first fetch completing.
    const servicesTabHasWarning =
        !!selectedRea &&
        reaServicesLoadedForId === selectedRea.id &&
        !isLoadingReaServices &&
        reaServices.flatMap((d) => d.services).length === 0;

    // ── Map callbacks ───────────────────────────────────────────────────────

    function handleCenterChanged(center: google.maps.LatLngLiteral) {
        setMapCenter(center);
        dispatch(loadBuildingsWithAssets({ location: center, radius: 1000 }));
    }

    function handleAddressSearch(center: google.maps.LatLngLiteral) {
        guardUnsavedChanges(() => {
            setMapCenter(center);
            dispatch(loadBuildingsWithAssets({ location: center, radius: 1000 }));
            setSelectedBuildings([]);
            dispatch(clearSelectedRea());
        });
    }

    function handleBuildingImported(location?: google.maps.LatLngLiteral | null) {
        dispatch(loadBuildingsWithAssets({ location: location ?? mapCenter, radius: 1000 }));
        dispatch(
            showSnackbar({ message: t('Building imported successfully'), severity: NotificationSeverity.Success }),
        );
        setImportDialogOpen(false);
    }

    function getReaSummaryForBuilding(buildingId: string) {
        return realEstateAssets.find((r) => r.buildings.some((b) => b.id === buildingId)) ?? null;
    }

    function getMunicipalityForBuilding(building: Building): string | null {
        // Building code format: "DE[FederalState][MunicipalityCode][BuildingCode]"
        // First 8 characters uniquely identify the municipality (e.g. "DENW23AL").
        return building.code?.length >= 8 ? building.code.substring(0, 8) : null;
    }

    function handleBuildingClicked(clicked: Building) {
        if (reaMode === 'editAssociations') {
            handleClickInEditAssociations(clicked);
        } else {
            handleClickNormal(clicked);
        }
    }

    function handleClickNormal(clicked: Building) {
        const isSelected = selectedBuildings.some((b) => b.id === clicked.id);
        const clickedRea = getReaSummaryForBuilding(clicked.id);

        if (isSelected) {
            if (clickedRea) {
                const remaining = selectedBuildings.filter((b) => !clickedRea.buildings.some((rb) => rb.id === b.id));
                if (remaining.length === 0) {
                    guardUnsavedChanges(() => setSelectedBuildings(remaining));
                } else {
                    setSelectedBuildings(remaining);
                }
            } else {
                const remaining = selectedBuildings.filter((b) => b.id !== clicked.id);
                if (remaining.length === 0) {
                    guardUnsavedChanges(() => setSelectedBuildings(remaining));
                } else {
                    setSelectedBuildings(remaining);
                }
            }
            return;
        }

        if (selectedBuildings.length === 0) {
            if (clickedRea) {
                const reaVisible = buildings.filter((b) => clickedRea.buildings.some((rb) => rb.id === b.id));
                setSelectedBuildings(reaVisible.length > 0 ? reaVisible : [clicked]);
            } else {
                setSelectedBuildings([clicked]);
            }
            return;
        }

        const existingRea = getReaSummaryForBuilding(selectedBuildings[0].id);

        if (existingRea && !clickedRea) {
            setConflictUnassociatedError(true);
            return;
        }
        if (!existingRea && clickedRea) {
            setConflictReaError(true);
            return;
        }
        if (existingRea && clickedRea && existingRea.id !== clickedRea.id) {
            setConflictReaError(true);
            return;
        }

        const existingMunicipality = getMunicipalityForBuilding(selectedBuildings[0]);
        const clickedMunicipality = getMunicipalityForBuilding(clicked);
        if (existingMunicipality && clickedMunicipality && existingMunicipality !== clickedMunicipality) {
            setConflictMunicipalityError(true);
            return;
        }

        if (isFarFromSelection(clicked, selectedBuildings)) {
            setDistanceWarningPendingBuilding(clicked);
            setDistanceWarningOpen(true);
            return;
        }

        setSelectedBuildings((prev) => [...prev, clicked]);
    }

    function handleClickInEditAssociations(clicked: Building) {
        const isSelected = selectedBuildings.some((b) => b.id === clicked.id);
        if (isSelected) {
            if (selectedBuildings.length <= 1) return;
            // Block removing a building that a saved meter still references.
            if (meterConnectedBuildingIds.has(clicked.id)) {
                setMeterConnectionError(true);
                return;
            }
            setSelectedBuildings((prev) => prev.filter((b) => b.id !== clicked.id));
            return;
        }
        const clickedRea = getReaSummaryForBuilding(clicked.id);
        if (clickedRea && clickedRea.id !== associatedReaSummary?.id) {
            setConflictReaError(true);
            return;
        }
        const existingMunicipality = selectedBuildings[0] ? getMunicipalityForBuilding(selectedBuildings[0]) : null;
        const clickedMunicipality = getMunicipalityForBuilding(clicked);
        if (existingMunicipality && clickedMunicipality && existingMunicipality !== clickedMunicipality) {
            setConflictMunicipalityError(true);
            return;
        }

        if (isFarFromSelection(clicked, selectedBuildings)) {
            setDistanceWarningPendingBuilding(clicked);
            setDistanceWarningOpen(true);
            return;
        }

        setSelectedBuildings((prev) => [...prev, clicked]);
    }

    function handleConfirmAddDifferentAddress() {
        setAddAddressWarningOpen(false);
        doActualSaveAssociations();
    }

    function handleConfirmDistanceWarning() {
        if (distanceWarningPendingBuilding) {
            setSelectedBuildings((prev) => [...prev, distanceWarningPendingBuilding]);
        }
        setDistanceWarningOpen(false);
        setDistanceWarningPendingBuilding(null);
    }

    function handleConfirmRemoveLosesAddress() {
        setRemoveAddressWarningOpen(false);
        setEditAssociationsNeedsNewAddress(true);
        setEditAssociationsNewAddress('');
    }

    const hasUnsavedTabChanges =
        isEditingName ||
        isEditingFunction ||
        isEditingNotes ||
        isEditingMainAddress ||
        isServicesTabEditing ||
        isDataCollectionDirty;

    function guardUnsavedChanges(action: () => void) {
        if (reaMode === 'create' || reaMode === 'editAssociations' || hasUnsavedTabChanges) {
            unsavedPendingRef.current = action;
            setUnsavedChangesOpen(true);
        } else {
            action();
        }
    }

    // ── REA actions ─────────────────────────────────────────────────────────

    function handleEnterCreate() {
        setNewReaName('');
        setNewReaFunctionCode('');
        setNewReaNotes('');
        setNewMainAddressSelection('');
        setNewCustomMainAddress('');
        setReaMode('create');
    }

    /**
     * Distinct building functions among the currently selected buildings on the map.
     * Used to populate the function dropdown of the "Create REA" form — the
     * newly-created REA must have a function present on at least one of its buildings.
     */
    const createFunctionOptions: { code: string; name: string }[] = useMemo(() => {
        const byCode = new Map<string, string>();
        for (const b of selectedBuildings) {
            if (b.buildingFunction?.code) {
                byCode.set(b.buildingFunction.code, b.buildingFunction.name ?? b.buildingFunction.code);
            }
        }
        return [...byCode.entries()].map(([code, name]) => ({ code, name }));
    }, [selectedBuildings]);

    function handleCancelCreate() {
        setReaMode('idle');
    }

    function handleSaveCreate() {
        if (!newReaName.trim() || selectedBuildings.length === 0) return;
        const mainAddress =
            newMainAddressSelection === '__other__' ? newCustomMainAddress.trim() : newMainAddressSelection;
        if (!mainAddress) return;
        const doSave = () => {
            dispatch(
                createRealEstateAsset({
                    name: newReaName.trim(),
                    mainAddress,
                    buildingIds: selectedBuildings.map((b) => b.id),
                    ...(newReaFunctionCode ? { buildingFunctionCode: newReaFunctionCode } : {}),
                    ...(newReaNotes.trim() ? { notes: newReaNotes.trim() } : {}),
                }),
            )
                .unwrap()
                .then((created) => {
                    dispatch(loadBuildingsWithAssets(lastLocationSearch ?? { location: INITIAL_CENTER, radius: 1000 }));
                    dispatch(fetchRealEstateAsset(created.id));
                    setReaMode('view');
                })
                .catch((err) => {
                    const message = getApiErrorMessage(err);
                    dispatch(showSnackbar({ message, severity: NotificationSeverity.Error }));
                });
        };

        const addresses = selectedBuildings
            .map((b) => buildingAddressMap.get(b.id))
            .filter((a): a is string => Boolean(a) && a !== '–');
        const unique = [...new Set(addresses)];
        createPendingRef.current = doSave;
        setCreateConfirmAddresses(unique);
        setCreateConfirmHasConflict(unique.length > 1);
        setCreateConfirmOpen(true);
    }

    function handleEnterEditAssociations() {
        void loadMeterConnectedBuildingIds(); // refresh saved meter connections before edits
        setEditAssociationsNeedsNewAddress(false);
        setEditAssociationsNewAddress('');
        setEditAssociationsNewCustomAddress('');
        setReaMode('editAssociations');
    }

    async function handleSaveAssociations() {
        if (!selectedRea || !associatedReaSummary) return;

        // Check 1: will removing buildings orphan the current main address?
        if (!editAssociationsNeedsNewAddress) {
            const newIds = new Set(selectedBuildings.map((b) => b.id));
            const remainingAddrs = [
                ...new Set(
                    selectedBuildings
                        .map((b) => buildingAddressMap.get(b.id))
                        .filter((a): a is string => Boolean(a) && a !== '–'),
                ),
            ];
            const removedAddrs = [
                ...new Set(
                    selectedRea.buildings
                        .filter((b) => !newIds.has(b.id))
                        .map((b) => buildingAddressMap.get(b.id))
                        .filter((a): a is string => Boolean(a) && a !== '–'),
                ),
            ];
            if (selectedRea.mainAddress && !new Set(remainingAddrs).has(selectedRea.mainAddress)) {
                setRemoveAddressWarningRemoved(removedAddrs);
                setRemoveAddressWarningRemaining(remainingAddrs);
                setRemoveAddressWarningOpen(true);
                return;
            }
        }

        // Check 2: are any newly added buildings bringing a new address not already in the REA?
        const currentReaIds = new Set(selectedRea.buildings.map((b) => b.id));
        const currentReaAddrs = new Set(
            selectedRea.buildings
                .map((b) => buildingAddressMap.get(b.id))
                .filter((a): a is string => Boolean(a) && a !== '–'),
        );
        const addedBuildings = selectedBuildings.filter((b) => !currentReaIds.has(b.id));
        const newAddrs = [
            ...new Set(
                addedBuildings
                    .map((b) => buildingAddressMap.get(b.id))
                    .filter((a): a is string => Boolean(a) && a !== '–'),
            ),
        ].filter((a) => !currentReaAddrs.has(a));
        if (newAddrs.length > 0) {
            setAddAddressWarningExisting([...currentReaAddrs]);
            setAddAddressWarningNew(newAddrs);
            setAddAddressWarningOpen(true);
            return;
        }

        await doActualSaveAssociations();
    }

    async function doActualSaveAssociations() {
        if (!selectedRea || !associatedReaSummary) return;
        const reaId = selectedRea.id;

        const resolvedNewAddress =
            editAssociationsNewAddress === '__other__'
                ? editAssociationsNewCustomAddress.trim()
                : editAssociationsNewAddress;
        if (editAssociationsNeedsNewAddress && resolvedNewAddress) {
            try {
                await dispatch(
                    updateRealEstateAsset({ assetId: reaId, request: { mainAddress: resolvedNewAddress } }),
                ).unwrap();
            } catch (err) {
                dispatch(showSnackbar({ message: getApiErrorMessage(err), severity: NotificationSeverity.Error }));
                return;
            }
        }

        const currentIds = new Set(selectedRea.buildings.map((b) => b.id));
        const newIds = new Set(selectedBuildings.map((b) => b.id));

        const toAdd = [...newIds].filter((id) => !currentIds.has(id));
        const toRemove = [...currentIds].filter((id) => !newIds.has(id));

        // There is no bulk endpoint; fire the individual add/remove calls in parallel
        // but use allSettled so a single failure does not cancel still-pending siblings.
        // Each failure produces an independent error snackbar, and we always refetch to
        // reconcile the UI with whatever partial state landed on the backend.
        const addResults = await Promise.allSettled(
            toAdd.map((bid) =>
                dispatch(addBuildingToAsset({ assetId: reaId, request: { buildingId: bid } }))
                    .unwrap()
                    .catch((err) => {
                        throw { buildingId: bid, op: 'add' as const, err };
                    }),
            ),
        );
        const removeResults = await Promise.allSettled(
            toRemove.map((bid) =>
                dispatch(removeBuildingFromAsset({ assetId: reaId, buildingId: bid }))
                    .unwrap()
                    .catch((err) => {
                        throw { buildingId: bid, op: 'remove' as const, err };
                    }),
            ),
        );

        const failures = [...addResults, ...removeResults].filter(
            (r): r is PromiseRejectedResult => r.status === 'rejected',
        );
        for (const f of failures) {
            const { op, buildingId, err } = f.reason as { op: 'add' | 'remove'; buildingId: string; err: unknown };
            const prefix = op === 'add' ? t('Failed to add building') : t('Failed to remove building');
            dispatch(
                showSnackbar({
                    message: `${prefix} ${buildingId.slice(0, 8)}: ${getApiErrorMessage(err)}`,
                    severity: NotificationSeverity.Error,
                }),
            );
        }

        // Always reconcile UI with actual backend state, whether the batch fully succeeded or not.
        dispatch(fetchRealEstateAsset(reaId));
        dispatch(loadBuildingsWithAssets(lastLocationSearch ?? { location: INITIAL_CENTER, radius: 1000 }));
        if (failures.length === 0) {
            setEditAssociationsNeedsNewAddress(false);
            setEditAssociationsNewAddress('');
            setReaMode('view');
        }
    }

    function handleCancelEditAssociations() {
        if (!selectedRea) return;
        const visible = buildings.filter((b) => selectedRea.buildings.some((rb) => rb.id === b.id));
        setSelectedBuildings(visible);
        setEditAssociationsNeedsNewAddress(false);
        setEditAssociationsNewAddress('');
        setEditAssociationsNewCustomAddress('');
        setReaMode('view');
    }

    function handleStartEditName() {
        if (!selectedRea) return;
        setEditNameValue(selectedRea.name);
        setIsEditingName(true);
    }

    function handleCancelEditName() {
        setIsEditingName(false);
    }

    function handleSaveEditName() {
        if (!selectedRea) return;
        const newName = editNameValue.trim();
        if (!newName || newName === selectedRea.name) {
            setIsEditingName(false);
            return;
        }
        dispatch(updateRealEstateAsset({ assetId: selectedRea.id, request: { name: newName } }))
            .unwrap()
            .then(() => setIsEditingName(false))
            .catch((err) => {
                const message = getApiErrorMessage(err);
                dispatch(showSnackbar({ message, severity: NotificationSeverity.Error }));
            });
    }

    function handleStartEditFunction() {
        if (!selectedRea) return;
        setEditFunctionCode(selectedRea.buildingFunctionCode ?? '');
        setIsEditingFunction(true);
    }

    function handleCancelEditFunction() {
        setIsEditingFunction(false);
    }

    function handleSaveEditFunction() {
        if (!selectedRea) return;
        const current = selectedRea.buildingFunctionCode ?? '';
        if (editFunctionCode === current) {
            setIsEditingFunction(false);
            return;
        }
        dispatch(
            updateRealEstateAsset({
                assetId: selectedRea.id,
                // Empty string clears the field on the backend (see update_real_estate_asset).
                request: { buildingFunctionCode: editFunctionCode },
            }),
        )
            .unwrap()
            .then(() => setIsEditingFunction(false))
            .catch((err) => {
                const message = getApiErrorMessage(err);
                dispatch(showSnackbar({ message, severity: NotificationSeverity.Error }));
            });
    }

    function handleStartEditNotes() {
        if (!selectedRea) return;
        setEditNotesValue(selectedRea.notes ?? '');
        setIsEditingNotes(true);
    }

    function handleCancelEditNotes() {
        setIsEditingNotes(false);
    }

    function handleSaveEditNotes() {
        if (!selectedRea) return;
        const current = selectedRea.notes ?? '';
        if (editNotesValue === current) {
            setIsEditingNotes(false);
            return;
        }
        dispatch(
            updateRealEstateAsset({
                assetId: selectedRea.id,
                request: { notes: editNotesValue },
            }),
        )
            .unwrap()
            .then(() => setIsEditingNotes(false))
            .catch((err) => {
                const message = getApiErrorMessage(err);
                dispatch(showSnackbar({ message, severity: NotificationSeverity.Error }));
            });
    }

    function handleStartEditMainAddress() {
        if (!selectedRea) return;
        // Derive whether the saved address matches a current building address
        // → preselect that option; otherwise treat it as "Other" with the
        // saved value pre-filled in the custom field.
        const saved = selectedRea.mainAddress ?? '';
        if (saved && reaAddressOptions.includes(saved)) {
            setEditMainAddressSelection(saved);
            setEditCustomMainAddress('');
        } else if (saved) {
            setEditMainAddressSelection('__other__');
            setEditCustomMainAddress(saved);
        } else {
            setEditMainAddressSelection('');
            setEditCustomMainAddress('');
        }
        setIsEditingMainAddress(true);
    }

    function handleCancelEditMainAddress() {
        setIsEditingMainAddress(false);
    }

    function handleSaveEditMainAddress() {
        if (!selectedRea) return;
        const newValue =
            editMainAddressSelection === '__other__' ? editCustomMainAddress.trim() : editMainAddressSelection;
        if (!newValue) return;
        if (newValue === (selectedRea.mainAddress ?? '')) {
            setIsEditingMainAddress(false);
            return;
        }
        dispatch(
            updateRealEstateAsset({
                assetId: selectedRea.id,
                request: { mainAddress: newValue },
            }),
        )
            .unwrap()
            .then(() => setIsEditingMainAddress(false))
            .catch((err) => {
                const message = getApiErrorMessage(err);
                dispatch(showSnackbar({ message, severity: NotificationSeverity.Error }));
            });
    }

    function getApiErrorMessage(err: unknown): string {
        if (err && typeof err === 'object' && 'status' in err) {
            const status = (err as { status: number }).status;
            if (status === 409) return t('This building is already assigned to another real estate asset.');
            if (status === 404) return t('Real estate asset not found.');
            if (status === 502 || status === 503) return t('Service temporarily unavailable. Please try again.');
        }
        return isProblemDetailTextOr(err, t('An unexpected error occurred.'));
    }

    // ── Render ──────────────────────────────────────────────────────────────

    const mapLayer = BuildingsDeckLayer(
        buildings,
        undefined,
        selectedBuildings,
        handleBuildingClicked,
        realEstateAssets,
        hoveredBuildingId,
        (newId) => setHoveredBuildingId((prev) => (prev === newId ? prev : newId)),
        reaTooltipMap,
    );

    // Top-level tab strip shows data-collection groups only; SERVICE_INFORMATION
    // groups render inside Services → Information instead (GEN-591).
    const topLevelGroups = useMemo(
        () => dataCollectionGroups.filter((g) => g.placement !== 'service_information'),
        [dataCollectionGroups],
    );

    // Building select options, shared by the data-collection tabs and the
    // Services → Information forms. Label by the building's position in the
    // REA — "(1/3)" — matching the map tooltip's second line when hovering.
    const buildingOptions = useMemo(
        () =>
            (selectedRea?.buildings ?? []).map((b, index, all) => {
                const position = `(${index + 1}/${all.length})`;
                const address = buildingAddressMap.get(b.id);
                return {
                    value: b.id,
                    label: address ? `${position} — ${stripCountry(address)}` : position,
                };
            }),
        [selectedRea, buildingAddressMap],
    );

    // Resolve lifted meter→building assignments into numbered marker points.
    // Gated on the group tab so markers only show while a data-collection tab
    // (e.g. Energy) is open; numbers combine when a building serves >1 meter.
    const meterMarkerPoints = useMemo<MeterMarkerPoint[]>(() => {
        if (!isGroupTab(activeReaTab) || meterMarkers.length === 0) return [];
        const numbersByBuilding = new Map<string, number[]>();
        for (const { meterNumber, buildingIds } of meterMarkers) {
            for (const id of buildingIds) {
                const arr = numbersByBuilding.get(id) ?? [];
                arr.push(meterNumber);
                numbersByBuilding.set(id, arr);
            }
        }
        const points: MeterMarkerPoint[] = [];
        for (const [id, numbers] of numbersByBuilding) {
            const building = buildings.find((b) => b.id === id);
            const center = building ? buildingCenter(building) : null;
            if (!center) continue;
            points.push({ position: [center.lng, center.lat], label: numbers.sort((a, b) => a - b).join(', ') });
        }
        return points;
    }, [meterMarkers, buildings, activeReaTab]);

    // Keep a stable wrapping array reference so <AdmiMap>'s DeckGlOverlay only
    // re-runs `deck.setProps({layers})` when the underlying layers actually change.
    const mapLayers = useMemo(
        () => [mapLayer, ...buildMeterMarkerLayers(meterMarkerPoints)],
        [mapLayer, meterMarkerPoints],
    );

    return (
        <Stack spacing={2} sx={{ p: 2 }}>
            <Typography variant="h5">{t('Real Estate Asset Management')}</Typography>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                {/* Left: map */}
                <Box sx={{ flex: 1 }}>
                    <AdmiMap
                        defaultCenter={INITIAL_CENTER}
                        onCenterChanged={handleCenterChanged}
                        onAddressSearch={handleAddressSearch}
                        panTo={deepLinkPanTo}
                        layers={mapLayers}
                        mapHeight="30rem"
                        cursor={hoveredBuildingId ? 'pointer' : undefined}
                        headerActions={
                            <Stack direction="row" spacing={1}>
                                <Button size="small" variant="outlined" onClick={() => setImportDialogOpen(true)}>
                                    {t('Import Building')}
                                </Button>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    disabled={selectedBuildings.length === 0}
                                    onClick={() => guardUnsavedChanges(() => setSelectedBuildings([]))}
                                >
                                    {t('Unselect all buildings')}
                                </Button>
                            </Stack>
                        }
                    />
                    {isLoadingBuildings && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                            <CircularProgress size={16} />
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {t('Loading…')}
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* Right: panel */}
                <Box
                    sx={{
                        flex: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        p: 2,
                        minHeight: 'calc(30rem + 48px)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        overflow: 'auto',
                    }}
                >
                    {/* ── REA section ── */}
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            {t('Real Estate Asset')}
                        </Typography>

                        {reaMode === 'idle' ? (
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                {t('Select a building to see its Real Estate Asset.')}
                            </Typography>
                        ) : reaMode === 'noRea' ? (
                            <Stack spacing={1}>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    {t('No associated Real Estate Asset')}
                                </Typography>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    sx={{ alignSelf: 'flex-start' }}
                                    onClick={handleEnterCreate}
                                >
                                    {t('Create new Real Estate Asset')}
                                </Button>
                            </Stack>
                        ) : reaMode === 'create' ? (
                            <Stack spacing={1.5}>
                                <TextField
                                    label={t('Name')}
                                    required
                                    size="small"
                                    fullWidth
                                    autoFocus
                                    value={newReaName}
                                    onChange={(e) => setNewReaName(e.target.value)}
                                />
                                <FormControl size="small" fullWidth>
                                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                                        {t('REA function')}
                                    </Typography>
                                    <Select
                                        value={newReaFunctionCode}
                                        displayEmpty
                                        onChange={(e) => setNewReaFunctionCode(e.target.value)}
                                    >
                                        <MenuItem value="">
                                            <em>{t('None')}</em>
                                        </MenuItem>
                                        {createFunctionOptions.map((o) => (
                                            <MenuItem key={o.code} value={o.code}>
                                                {o.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <TextField
                                    label={t('General notes')}
                                    size="small"
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    value={newReaNotes}
                                    onChange={(e) => setNewReaNotes(e.target.value)}
                                />
                                <MainAddressSelector
                                    value={newMainAddressSelection}
                                    customValue={newCustomMainAddress}
                                    options={selectedAddressOptions}
                                    onChange={setNewMainAddressSelection}
                                    onCustomChange={setNewCustomMainAddress}
                                />
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={handleSaveCreate}
                                        disabled={
                                            !newReaName.trim() ||
                                            selectedBuildings.length === 0 ||
                                            !newMainAddressSelection ||
                                            (newMainAddressSelection === '__other__' && !newCustomMainAddress.trim()) ||
                                            isMutating
                                        }
                                    >
                                        {t('Save and create')}
                                    </Button>
                                    <Button variant="outlined" size="small" onClick={handleCancelCreate}>
                                        {t('Cancel')}
                                    </Button>
                                </Box>
                            </Stack>
                        ) : isLoadingRea ? (
                            <CircularProgress size={20} />
                        ) : selectedRea ? (
                            <>
                                <Stack spacing={0} sx={{ mb: 1 }}>
                                    <Typography
                                        variant="body1"
                                        sx={{ lineHeight: 1.3, letterSpacing: 0, fontWeight: 'bold' }}
                                    >
                                        {selectedRea.name}
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{ color: 'text.secondary', lineHeight: 1.3, letterSpacing: 0 }}
                                    >
                                        {selectedBuildings[0]
                                            ? (buildingMunicipalityMap.get(selectedBuildings[0].id) ?? t('Loading…'))
                                            : '–'}
                                    </Typography>
                                </Stack>

                                {/* editAssociations header */}
                                {reaMode === 'editAssociations' ? (
                                    <Stack spacing={1.5}>
                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                            {t(
                                                'Click buildings on the map to add or remove them. At least one building must remain.',
                                            )}
                                        </Typography>
                                        {editAssociationsNeedsNewAddress && (
                                            <MainAddressSelector
                                                value={editAssociationsNewAddress}
                                                customValue={editAssociationsNewCustomAddress}
                                                options={selectedAddressOptions}
                                                onChange={setEditAssociationsNewAddress}
                                                onCustomChange={setEditAssociationsNewCustomAddress}
                                            />
                                        )}
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <Button
                                                variant="contained"
                                                size="small"
                                                onClick={handleSaveAssociations}
                                                disabled={
                                                    selectedBuildings.length === 0 ||
                                                    isMutating ||
                                                    (editAssociationsNeedsNewAddress &&
                                                        (!editAssociationsNewAddress ||
                                                            (editAssociationsNewAddress === '__other__' &&
                                                                !editAssociationsNewCustomAddress.trim())))
                                                }
                                            >
                                                {t('Save')}
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                onClick={handleCancelEditAssociations}
                                            >
                                                {t('Cancel')}
                                            </Button>
                                        </Box>
                                    </Stack>
                                ) : (
                                    <>
                                        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1.5 }}>
                                            <Tabs
                                                value={tabKey(activeReaTab)}
                                                onChange={(_, key: string) => {
                                                    const next: ReaTab = key.startsWith('group:')
                                                        ? { kind: 'group', code: key.slice('group:'.length) }
                                                        : (key as StaticReaTab);
                                                    guardUnsavedChanges(() => setActiveReaTab(next));
                                                }}
                                                variant="scrollable"
                                                scrollButtons="auto"
                                                sx={{
                                                    '& .MuiTabs-scrollButtons.Mui-disabled': {
                                                        opacity: 0,
                                                        width: 0,
                                                    },
                                                }}
                                            >
                                                <Tab value="general" label={t('General')} />
                                                {topLevelGroups.map((g) => (
                                                    <Tab key={g.code} value={`group:${g.code}`} label={t(g.name)} />
                                                ))}
                                                <Tab
                                                    value="services"
                                                    label={
                                                        <TabLabel
                                                            label={t('Services')}
                                                            warning={servicesTabHasWarning}
                                                        />
                                                    }
                                                />
                                                <Tab value="steckbriefe" label={t('Steckbriefe')} />
                                                <Tab value="history" label={t('History')} />
                                            </Tabs>
                                            {isLoadingDataCollectionGroups && (
                                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', py: 0.5 }}>
                                                    <CircularProgress size={12} />
                                                </Box>
                                            )}
                                        </Box>

                                        {/* General */}
                                        {activeReaTab === 'general' && (
                                            <Box
                                                sx={{
                                                    display: 'grid',
                                                    gridTemplateColumns: 'auto 1fr',
                                                    columnGap: 2,
                                                    rowGap: 1,
                                                    alignItems: 'baseline',
                                                }}
                                            >
                                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                    {t('Name')} *
                                                </Typography>
                                                {isEditingName ? (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <TextField
                                                            size="small"
                                                            autoFocus
                                                            value={editNameValue}
                                                            onChange={(e) => setEditNameValue(e.target.value)}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') handleSaveEditName();
                                                                if (e.key === 'Escape') handleCancelEditName();
                                                            }}
                                                            disabled={isMutating}
                                                        />
                                                        <IconButton
                                                            size="small"
                                                            onClick={handleSaveEditName}
                                                            disabled={!editNameValue.trim() || isMutating}
                                                            aria-label={t('Save')}
                                                        >
                                                            <Check fontSize="small" />
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            onClick={handleCancelEditName}
                                                            disabled={isMutating}
                                                            aria-label={t('Cancel')}
                                                        >
                                                            <Close fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                ) : (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                                            {selectedRea.name}
                                                        </Typography>
                                                        <IconButton
                                                            size="small"
                                                            onClick={handleStartEditName}
                                                            aria-label={t('Edit')}
                                                        >
                                                            <Edit fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                )}
                                                {/* Main address — required, label in col 1, edit UI in col 2 */}
                                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                    {t('Main address')} *
                                                </Typography>
                                                {isEditingMainAddress ? (
                                                    <Stack spacing={1}>
                                                        <MainAddressSelector
                                                            value={editMainAddressSelection}
                                                            customValue={editCustomMainAddress}
                                                            options={reaAddressOptions}
                                                            onChange={setEditMainAddressSelection}
                                                            onCustomChange={setEditCustomMainAddress}
                                                            showLabel={false}
                                                            disabled={isMutating}
                                                            autoFocusSelect={editMainAddressSelection !== '__other__'}
                                                            autoFocusCustomField
                                                            onCustomKeyDown={(e) => {
                                                                if (e.key === 'Escape') handleCancelEditMainAddress();
                                                            }}
                                                        />
                                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                                            <Button
                                                                size="small"
                                                                variant="contained"
                                                                disabled={
                                                                    isMutating ||
                                                                    !editMainAddressSelection ||
                                                                    (editMainAddressSelection === '__other__' &&
                                                                        !editCustomMainAddress.trim())
                                                                }
                                                                onClick={handleSaveEditMainAddress}
                                                            >
                                                                {t('Save')}
                                                            </Button>
                                                            <Button
                                                                size="small"
                                                                onClick={handleCancelEditMainAddress}
                                                                disabled={isMutating}
                                                            >
                                                                {t('Cancel')}
                                                            </Button>
                                                        </Box>
                                                    </Stack>
                                                ) : (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{ wordBreak: 'break-word' }}
                                                            color={
                                                                selectedRea.mainAddress ? undefined : 'text.disabled'
                                                            }
                                                        >
                                                            {selectedRea.mainAddress || t('Select main address')}
                                                        </Typography>
                                                        <IconButton
                                                            size="small"
                                                            onClick={handleStartEditMainAddress}
                                                            aria-label={t('Edit')}
                                                        >
                                                            <Edit fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                )}

                                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                    {t('REA function')}
                                                </Typography>
                                                {isEditingFunction ? (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <FormControl size="small" sx={{ minWidth: 200 }}>
                                                            <Select
                                                                autoFocus
                                                                value={editFunctionCode}
                                                                displayEmpty
                                                                disabled={isMutating}
                                                                onChange={(e) => setEditFunctionCode(e.target.value)}
                                                            >
                                                                <MenuItem value="">
                                                                    <em>{t('None')}</em>
                                                                </MenuItem>
                                                                {reaFunctionOptions.map((o) => (
                                                                    <MenuItem key={o.code} value={o.code}>
                                                                        {o.name}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                        <IconButton
                                                            size="small"
                                                            onClick={handleSaveEditFunction}
                                                            disabled={isMutating}
                                                            aria-label={t('Save')}
                                                        >
                                                            <Check fontSize="small" />
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            onClick={handleCancelEditFunction}
                                                            disabled={isMutating}
                                                            aria-label={t('Cancel')}
                                                        >
                                                            <Close fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                ) : selectedReaFunctionName ? (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                                            {selectedReaFunctionName}
                                                        </Typography>
                                                        <IconButton
                                                            size="small"
                                                            onClick={handleStartEditFunction}
                                                            aria-label={t('Edit')}
                                                        >
                                                            <Edit fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                ) : (
                                                    <Box>
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            onClick={handleStartEditFunction}
                                                        >
                                                            {t('Select function')}
                                                        </Button>
                                                    </Box>
                                                )}
                                                <InfoRow
                                                    label={t('No. of buildings')}
                                                    value={selectedRea.buildings.length}
                                                />

                                                {/* Building addresses */}
                                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                    {t('Building addresses')}
                                                </Typography>
                                                <Box>
                                                    <Stack spacing={0.25}>
                                                        {reaDisplayAddresses.map((address) => (
                                                            <Typography key={address} variant="body2">
                                                                {address}
                                                            </Typography>
                                                        ))}
                                                    </Stack>
                                                </Box>

                                                {/* General notes */}
                                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                    {t('General notes')}
                                                </Typography>
                                                {isEditingNotes ? (
                                                    <Stack spacing={0.5}>
                                                        <TextField
                                                            size="small"
                                                            fullWidth
                                                            multiline
                                                            minRows={3}
                                                            autoFocus
                                                            value={editNotesValue}
                                                            onChange={(e) => setEditNotesValue(e.target.value)}
                                                            slotProps={{
                                                                input: {
                                                                    onKeyDown: (e) => {
                                                                        if (
                                                                            e.key === 'Enter' &&
                                                                            (e.metaKey || e.ctrlKey)
                                                                        ) {
                                                                            handleSaveEditNotes();
                                                                        }
                                                                        if (e.key === 'Escape') handleCancelEditNotes();
                                                                    },
                                                                },
                                                            }}
                                                            disabled={isMutating}
                                                        />
                                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                                            <Button
                                                                size="small"
                                                                variant="contained"
                                                                onClick={handleSaveEditNotes}
                                                                disabled={isMutating}
                                                            >
                                                                {t('Save')}
                                                            </Button>
                                                            <Button
                                                                size="small"
                                                                onClick={handleCancelEditNotes}
                                                                disabled={isMutating}
                                                            >
                                                                {t('Cancel')}
                                                            </Button>
                                                        </Box>
                                                    </Stack>
                                                ) : (
                                                    <Box
                                                        onClick={handleStartEditNotes}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter' || e.key === ' ') {
                                                                e.preventDefault();
                                                                handleStartEditNotes();
                                                            }
                                                        }}
                                                        tabIndex={0}
                                                        role="button"
                                                        aria-label={t('Edit general notes')}
                                                        sx={{
                                                            border: '1px solid',
                                                            borderColor: 'divider',
                                                            borderRadius: 1,
                                                            p: 1.5,
                                                            minHeight: 64,
                                                            cursor: 'text',
                                                            '&:hover': { borderColor: 'text.secondary' },
                                                            '&:focus-visible': {
                                                                outline: '2px solid',
                                                                outlineColor: 'primary.main',
                                                                outlineOffset: '2px',
                                                            },
                                                        }}
                                                    >
                                                        {selectedRea.notes ? (
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    whiteSpace: 'pre-wrap',
                                                                    wordBreak: 'break-word',
                                                                }}
                                                            >
                                                                {selectedRea.notes}
                                                            </Typography>
                                                        ) : (
                                                            <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                                                                {t('No notes')}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                )}

                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    sx={{ gridColumn: '1 / -1', alignSelf: 'flex-start' }}
                                                    onClick={handleEnterEditAssociations}
                                                >
                                                    {t('Add or Remove Buildings')}
                                                </Button>
                                            </Box>
                                        )}

                                        {/* Data collection groups (dynamic, one tab per requirement-group `code`) */}
                                        {isGroupTab(activeReaTab) && (
                                            <DataCollectionTab
                                                realEstateAssetId={selectedRea.id}
                                                groupCode={activeReaTab.code}
                                                reaServices={reaServices}
                                                buildingOptions={buildingOptions}
                                                groups={dataCollectionGroups}
                                                onDirtyChange={setIsDataCollectionDirty}
                                                onBuildingMarkersChange={setMeterMarkers}
                                            />
                                        )}

                                        {/* Services */}
                                        {activeReaTab === 'services' && (
                                            <ServicesTab
                                                realEstateAsset={selectedRea}
                                                dataCollectionGroups={dataCollectionGroups}
                                                buildingOptions={buildingOptions}
                                                onEditingChange={setIsServicesTabEditing}
                                            />
                                        )}

                                        {/* Steckbriefe (EnSan, GEN-700) */}
                                        {activeReaTab === 'steckbriefe' && (
                                            <SteckbriefeTab
                                                realEstateAsset={selectedRea}
                                                buildingOptions={buildingOptions}
                                            />
                                        )}

                                        {/* History */}
                                        {activeReaTab === 'history' && <HistoryTab assetId={selectedRea.id} />}
                                    </>
                                )}
                            </>
                        ) : null}
                    </Box>

                    {/* ── Building selection strip ── */}
                    {reaMode !== 'idle' && selectedBuildings.length > 0 && activeReaTab === 'general' && (
                        <>
                            <Divider />
                            <Box>
                                <Typography variant="h6" sx={{ mb: 1 }}>
                                    {t('Selected buildings')}
                                </Typography>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <IconButton
                                        size="small"
                                        onClick={() => setCurrentBuildingIndex((i) => i - 1)}
                                        disabled={currentBuildingIndex === 0}
                                    >
                                        <ArrowBackIos fontSize="small" />
                                    </IconButton>
                                    <Typography variant="body2" sx={{ minWidth: 40, textAlign: 'center' }}>
                                        {currentBuildingIndex + 1} / {selectedBuildings.length}
                                    </Typography>
                                    <IconButton
                                        size="small"
                                        onClick={() => setCurrentBuildingIndex((i) => i + 1)}
                                        disabled={currentBuildingIndex === selectedBuildings.length - 1}
                                    >
                                        <ArrowForwardIos fontSize="small" />
                                    </IconButton>
                                </Box>

                                <Divider sx={{ mb: 1.5 }} />

                                {currentBuilding && (
                                    <Box
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: 'auto 1fr',
                                            columnGap: 2,
                                            rowGap: 1,
                                            alignItems: 'baseline',
                                        }}
                                    >
                                        <InfoRow label={t('Function')} value={currentBuilding.buildingFunction?.name} />
                                        <InfoRow
                                            label={t('Address')}
                                            value={buildingAddressMap.get(currentBuilding.id) ?? t('Loading…')}
                                        />
                                        <InfoRow label={t('Building ID')} value={currentBuilding.id} />
                                    </Box>
                                )}
                            </Box>
                        </>
                    )}
                </Box>
            </Box>

            {/* Error snackbars */}
            <Snackbar
                open={conflictReaError}
                autoHideDuration={4000}
                onClose={() => setConflictReaError(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity="error" onClose={() => setConflictReaError(false)}>
                    {t('Cannot select this real estate asset. Un-select the current buildings first.')}
                </Alert>
            </Snackbar>

            <Snackbar
                open={conflictUnassociatedError}
                autoHideDuration={4000}
                onClose={() => setConflictUnassociatedError(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity="error" onClose={() => setConflictUnassociatedError(false)}>
                    {t(
                        'Cannot select a building that is not a part of this real estate asset. Unselect this real estate asset first.',
                    )}
                </Alert>
            </Snackbar>

            <Snackbar
                open={conflictMunicipalityError}
                autoHideDuration={5000}
                onClose={() => setConflictMunicipalityError(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity="error" onClose={() => setConflictMunicipalityError(false)}>
                    {t('Cannot select buildings from multiple municipalities. Unselect the current buildings first.')}
                </Alert>
            </Snackbar>

            <Snackbar
                open={meterConnectionError}
                autoHideDuration={6000}
                onClose={() => setMeterConnectionError(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity="error" onClose={() => setMeterConnectionError(false)}>
                    {t(
                        'Cannot remove a building that is connected to a meter. Please go to the "Energy" tab and remove this building from the meter first.',
                    )}
                </Alert>
            </Snackbar>

            {/* Warning when adding a building more than 100 m from the current selection */}
            <Dialog
                open={distanceWarningOpen}
                onClose={() => {
                    setDistanceWarningOpen(false);
                    setDistanceWarningPendingBuilding(null);
                }}
            >
                <DialogTitle>{t('Building is far away')}</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mt: 0.5 }}>
                        {t(
                            'This building is more than 100 m away from the other selected buildings. Are you sure it belongs to the same real estate asset?',
                        )}
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setDistanceWarningOpen(false);
                            setDistanceWarningPendingBuilding(null);
                        }}
                    >
                        {t('Cancel')}
                    </Button>
                    <Button variant="contained" color="warning" onClick={handleConfirmDistanceWarning}>
                        {t('Add anyway')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add building with different address warning */}
            <Dialog open={addAddressWarningOpen} onClose={() => setAddAddressWarningOpen(false)}>
                <DialogTitle>{t('Add building with different address?')}</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 0.5 }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {t('Current buildings')}:
                        </Typography>
                        <Box component="ul" sx={{ mt: 0.5, pl: 2.5 }}>
                            {addAddressWarningExisting.map((addr) => (
                                <Typography key={addr} component="li" variant="body2">
                                    {addr}
                                </Typography>
                            ))}
                        </Box>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                            {t('Building being added')}:
                        </Typography>
                        <Box component="ul" sx={{ mt: 0.5, pl: 2.5 }}>
                            {addAddressWarningNew.map((addr) => (
                                <Typography key={addr} component="li" variant="body2">
                                    {addr}
                                </Typography>
                            ))}
                        </Box>
                    </Box>
                    <Alert severity="warning" sx={{ mt: 1.5, py: 0.5 }}>
                        {t(
                            'These buildings have different addresses. Are you sure they belong to the same real estate asset?',
                        )}
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddAddressWarningOpen(false)}>{t('Cancel')}</Button>
                    <Button variant="contained" color="warning" onClick={handleConfirmAddDifferentAddress}>
                        {t('Add anyway')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Remove buildings loses main address warning */}
            <Dialog open={removeAddressWarningOpen} onClose={() => setRemoveAddressWarningOpen(false)}>
                <DialogTitle>{t('Remove buildings and change main address?')}</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 0.5 }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {t('Buildings being removed')}:
                        </Typography>
                        <Box component="ul" sx={{ mt: 0.5, pl: 2.5 }}>
                            {removeAddressWarningRemoved.map((addr) => (
                                <Typography key={addr} component="li" variant="body2">
                                    {addr}
                                </Typography>
                            ))}
                        </Box>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                            {t('Buildings remaining')}:
                        </Typography>
                        <Box component="ul" sx={{ mt: 0.5, pl: 2.5 }}>
                            {removeAddressWarningRemaining.map((addr) => (
                                <Typography key={addr} component="li" variant="body2">
                                    {addr}
                                </Typography>
                            ))}
                        </Box>
                    </Box>
                    <Alert severity="warning" sx={{ mt: 1.5, py: 0.5 }}>
                        <Trans
                            i18nKey="The current main address <strong>{{address}}</strong> will no longer be present in the remaining buildings. You will need to select a new main address before saving."
                            values={{ address: selectedRea?.mainAddress ?? '' }}
                            components={{ strong: <strong /> }}
                        />
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRemoveAddressWarningOpen(false)}>{t('Cancel')}</Button>
                    <Button variant="contained" color="warning" onClick={handleConfirmRemoveLosesAddress}>
                        {t('Save anyway')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Unsaved changes */}
            <Dialog open={unsavedChangesOpen} onClose={() => setUnsavedChangesOpen(false)}>
                <DialogTitle>{t('Unsaved changes')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>{t('You have unsaved changes. Do you want to discard them?')}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUnsavedChangesOpen(false)}>{t('Keep editing')}</Button>
                    <Button
                        variant="contained"
                        color="warning"
                        onClick={() => {
                            setIsEditingName(false);
                            setIsEditingFunction(false);
                            setIsEditingNotes(false);
                            setIsEditingMainAddress(false);
                            setIsServicesTabEditing(false);
                            setIsDataCollectionDirty(false);
                            unsavedPendingRef.current?.();
                            unsavedPendingRef.current = null;
                            setUnsavedChangesOpen(false);
                            if (reaMode === 'create' || reaMode === 'editAssociations') setReaMode('idle');
                        }}
                    >
                        {t('Discard changes')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Create confirmation */}
            <Dialog open={createConfirmOpen} onClose={() => setCreateConfirmOpen(false)}>
                <DialogTitle>{t('Create real estate asset?')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t(
                            'Are you sure you want to create a real estate asset with the following information? You cannot delete the real estate asset later.',
                        )}
                    </DialogContentText>
                    <Box sx={{ mt: 1.5 }}>
                        <Typography variant="body2">
                            <strong>{t('Name')}:</strong> {newReaName.trim()}
                        </Typography>
                        <Typography variant="body2">
                            <strong>{t('Number of buildings')}:</strong> {selectedBuildings.length}
                        </Typography>
                    </Box>
                    {createConfirmAddresses.length > 0 && (
                        <Box component="ul" sx={{ mt: 1, pl: 2.5 }}>
                            {createConfirmAddresses.map((address) => (
                                <Typography key={address} component="li" variant="body2">
                                    {address}
                                </Typography>
                            ))}
                        </Box>
                    )}
                    {createConfirmHasConflict && (
                        <Alert severity="warning" sx={{ mt: 1.5, py: 0.5 }}>
                            {t(
                                'These buildings have different addresses. Are you sure they belong to the same real estate asset?',
                            )}
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateConfirmOpen(false)}>{t('Cancel')}</Button>
                    <Button
                        variant="contained"
                        disabled={isMutating}
                        onClick={() => {
                            setCreateConfirmOpen(false);
                            createPendingRef.current?.();
                            createPendingRef.current = null;
                        }}
                    >
                        {t('Create')}
                    </Button>
                </DialogActions>
            </Dialog>

            <ImportBuildingDialog
                open={importDialogOpen}
                onClose={() => setImportDialogOpen(false)}
                initialCenter={mapCenter}
                onImported={handleBuildingImported}
            />
        </Stack>
    );
}
